/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Button, Checkbox, Col, Row } from 'react-bootstrap';

import { Cell, Column, Table } from 'fixed-data-table-2';
import * as d3 from 'd3';
import s from './TissueExpressionHeatmap.css';

import { saveText } from '../../core/save';

import PredictionCell from '../PredictionCell';
import Legend from '../Legend';
import Tooltip from '../Tooltip';

const color = d3
  .scaleLinear()
  .domain([0, 0.5, 1])
  .range(['#fee8c8', '#fdbb84', '#e34a33']);

/*
 * Cell showing a 'bar' plot of the confidence
 */
const BarProbCell = ({ rowIndex, terms, data, onClick, ...props }) => (
  <PredictionCell
    col="score"
    data={data}
    rowIndex={terms[rowIndex].identifier}
    color={() => '#777'}
    {...props}
  />
);

/*
 * Colored cell showing the confidence
 */
const ProbCell = React.memo(
  ({
    onClick,
    rowIndex,
    colIndex,
    terms,
    data,
    selected,
    selectedLeft,
    border,
    ...rest
  }) => {
    const term = terms[rowIndex];
    const pred = term && data ? data[term.identifier] : null;
    return (
      <Cell
        onClick={onClick}
        className={s.cell}
        style={{
          background: pred && pred.score ? color(pred.score) : '#eee',
          borderRight: selected || selectedLeft ? border : '1px solid #ddd',
          borderTop: !rowIndex ? border : '1px solid #ddd',
          borderBottom: rowIndex === terms.length - 1 ? border : 'none',
          borderLeft:
            selected && !colIndex
              ? border
              : !colIndex
              ? '1px solid #ddd'
              : 'none',
        }}
        {...rest}
      />
    );
  },
);

/*
 * Cell displaying the tissue name
 */
const TissueCell = ({ rowIndex, terms, slim, ...props }) => {
  const term = terms[rowIndex];
  const color =
    term.slim_terms && term.slim_terms.length
      ? slim[term.slim_terms[0].title].color
      : '#aaa';

  return (
    <Cell
      style={{
        textAlign: 'right',
        borderRight: `6px solid ${color}`,
      }}
      {...props}
    >
      {terms[rowIndex].title}
    </Cell>
  );
};

const SlimTissueCell = ({ rowIndex, terms, ...props }) => (
  <Cell {...props}>{terms[rowIndex].title}</Cell>
);

const SlimSelectCell = ({ rowIndex, terms, onClick, ...props }) => (
  <Cell
    style={{
      textAlign: 'center',
      opacity: '.7',
    }}
    {...props}
  >
    <Checkbox
      onChange={event => onClick(event, terms[rowIndex])}
      style={{
        padding: '4px 0 0 7px',
        margin: '0px',
        background: terms[rowIndex].color,
      }}
    />
  </Cell>
);

// Calculate average prediction of a term
const average = (terms, predictions) => {
  const termScores = terms.map(term => {
    // Filter for genes with term prediction, return prediction
    const preds = term
      ? Object.keys(predictions)
          // .filter(gene=>predictions[gene][term.identifier])
          // Missing predictions have scores of zero. They are likely low
          // confidence predictions and should decrease the overall average.
          .map(gene => predictions[gene][term.identifier] || { score: 0 })
      : [];

    // Calculate mean
    return (
      preds.map(pred => pred.score).reduce((a, b) => a + b, 0) / preds.length
    );
  });
  // Reduce to object of term id => score avg
  return termScores.reduce((obj, avg, idx) => {
    return {
      ...obj,
      [terms[idx].identifier]: { score: avg },
    };
  }, {});
};

const getMaxes = (terms, predictions) => {
  let maxes = terms.map(term => {
    // Filter for genes with term prediction, return prediction
    const preds = term
      ? Object.keys(predictions)
          .filter(gene => predictions[gene][term.identifier])
          .map(gene => predictions[gene][term.identifier])
      : [];
    // Calculate max of score. Score will equal 1 if it corresponds to
    // a known annotation.
    return Math.max(...preds.map(pred => pred.score));
  });
  // Reduce to object of term id => max score
  maxes = maxes.reduce((obj, max, idx) => {
    return {
      ...obj,
      [terms[idx].identifier]: { score: max },
    };
  }, {});
  return maxes;
};

/*
 * Component to display the tissue expression of many genes by many tissues
 * as a heatmap.
 */
const TissueExpressionHeatmap = ({
  genes = [],
  predictions = {},
  terms = [],
  minScore = 0.05,
}) => {
  const [barScores, setBarScores] = useState(null);
  const [selectedGene, setSelectedGene] = useState(null);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [avgs, setAvgs] = useState(null);
  const [maxes, setMaxes] = useState({});
  const [slimTerms, setSlimTerms] = useState({});
  const [slimList, setSlimList] = useState([]);
  const [filteredTerms, setFilteredTerms] = useState([]);

  useEffect(() => {
    setAvgs(average(terms, predictions));
    setMaxes(getMaxes(terms, predictions));
  }, [terms, predictions]);

  // Filter terms by the selected slim(s)
  const slimFilter = useCallback(
    term => {
      if (!Object.keys(filters).length) return true;
      if (term.slim_terms && term.slim_terms.length)
        return filters[term.slim_terms[0].title];
      return false;
    },
    [filters],
  );

  // Sort tissues by selected gene or average
  const tissueSort = useCallback(
    (a, b) => {
      // If not gene selected, use average
      if (!sort) return avgs[b.identifier].score - avgs[a.identifier].score;
      // If both terms have scores, sort by score value
      if (sort[a.identifier] && sort[b.identifier])
        return sort[b.identifier].score - sort[a.identifier].score;
      // Else, the term with the score is higher
      return sort[a.identifier] ? -1 : 1;
    },
    [avgs, sort],
  );

  // Call to return a string txt for the table
  const downloadTable = () => {
    let header = 'TISSUE';
    for (let i = 0; i < genes.length; i += 1) {
      header += `,${genes[i].standard_name}`;
    }
    if (genes.length > 1) {
      header += ',AVERAGE';
    }
    header += '\n';

    let body = '';
    for (let i = 0; i < filteredTerms.length; i += 1) {
      const termID = filteredTerms[i].identifier;
      body += `${filteredTerms[i].title},`;
      for (let j = 0; j < genes.length; j += 1) {
        const gene = genes[j].entrez;
        if (predictions[gene] && predictions[gene][termID]) {
          body += `${predictions[gene][termID].score.toFixed(2)},`;
        } else {
          body += 'N/A,';
        }
      }
      if (genes.length > 1) {
        body += avgs[termID].score.toFixed(2);
      }
      body += '\n';
    }
    return header + body;
  };

  const filterTerms = useCallback(
    (terms, filters) => {
      const newFilteredTerms = terms
        .filter(term => maxes[term.identifier]?.score > minScore)
        .filter(term => {
          if (!Object.keys(filters).length) return true;
          if (term.slim_terms && term.slim_terms.length)
            return filters[term.slim_terms[0].title];
          return false;
        })
        .sort(tissueSort);
      setFilteredTerms(newFilteredTerms);
    },
    [maxes, minScore, tissueSort],
  );

  // Add/remove a slim filter
  const handleSlimSelect = useCallback(
    (term, add) => {
      const tmpFilters = Object.assign({}, filters);
      if (add) tmpFilters[term.title] = term;
      else delete tmpFilters[term.title];
      setFilters(tmpFilters);

      // Filter by selected slim, then sort by average
      filterTerms(terms, tmpFilters);
    },
    [filterTerms, filters, terms],
  );

  // Update selected gene and displayed bar when a gene is selected
  // If gene is null, show the average
  const handleGeneSelect = useCallback(
    (gene, predictionsParam) => {
      let tmpPredictions = predictionsParam;
      if (!predictionsParam) tmpPredictions = predictions;
      setSelectedGene(gene);
      setBarScores(gene ? tmpPredictions[gene.entrez] : avgs);
    },
    [avgs, predictions],
  );

  useEffect(() => {
    // If there's only one gene, always select it
    if (genes.length === 1) handleGeneSelect(genes[0], predictions);
  }, [genes]);

  // Update the sort to the selected gene
  const handleSort = useCallback(() => {
    let sortGene = null;
    if (selectedGene) sortGene = predictions[selectedGene.entrez];
    filterTerms(terms, filters);
    setSort(sortGene);
  }, [filterTerms, filters, predictions, selectedGene, terms]);

  useEffect(() => {
    // Gather all the slim terms
    const tmpSlimTerms = {};
    terms.forEach(term => {
      if (term.slim_terms)
        term.slim_terms.forEach(slimTerm => {
          tmpSlimTerms[slimTerm.title] = slimTerm;
        });
    });

    // Assign a color to each slim term
    const slimNames = Object.keys(tmpSlimTerms).sort();
    slimNames.forEach((key, idx) => {
      tmpSlimTerms[key].color = d3.scaleOrdinal(d3.schemeCategory10).range()[
        idx
      ];
    });
    const tmpSlimList = slimNames.map(term => tmpSlimTerms[term]);

    setSlimTerms(tmpSlimTerms);
    setSlimList(tmpSlimList);
    // Filter and sort new terms
    filterTerms(terms, filters);
  }, [terms]);

  // A column for each gene
  const cols = (gene, idx) => {
    return (
      <Column
        key={gene}
        header={
          <Cell className={s.geneHeader} onClick={() => handleGeneSelect(gene)}>
            <Tooltip
              text={`Tissue/cell-type expression of ${gene.standard_name}`}
            >
              {gene.standard_name}
            </Tooltip>
          </Cell>
        }
        width={40}
        cell={
          <ProbCell
            colIndex={idx}
            selected={selectedGene === gene}
            selectedLeft={
              (idx + 1 < genes.length && selectedGene === genes[idx + 1]) ||
              (!selectedGene && idx + 1 === genes.length)
            }
            terms={filteredTerms}
            border={genes.length === 1 ? '1px solid #ddd' : '3px solid #555'}
            onClick={() => handleGeneSelect(gene)}
            data={predictions[gene.entrez]}
          />
        }
      />
    );
  };

  return (
    <div className={s.root}>
      <div className={s.button}>
        <Button
          bsStyle="primary"
          onClick={() => {
            saveText(downloadTable(), 'TissueExpressionHeatmap.txt');
          }}
        >
          <Tooltip text="Download predicted tissue expression">
            <i className="fa fa-download" /> Download predictions
          </Tooltip>
        </Button>
      </div>
      <Row>
        <Col md={3}>
          <div
            style={{ textAlign: 'center', width: '210px', margin: '30px 0' }}
          >
            <p>
              <strong>CONFIDENCE</strong>
            </p>
            <Legend width={160} height={25} color={color} />
          </div>
          <Table
            rowHeight={35}
            rowsCount={slimList.length}
            width={210}
            height={slimList.length * 35 + 80}
            rowClassNameGetter={() => s.row}
            headerHeight={55}
          >
            <Column
              width={40}
              cell={
                <SlimSelectCell
                  onClick={(event, term) =>
                    handleSlimSelect(term, event.target.checked)
                  }
                  terms={slimList}
                />
              }
            />
            <Column
              width={150}
              header={
                <Cell style={{ textAlign: 'left' }}>
                  FILTER BY TISSUE SYSTEM
                </Cell>
              }
              cell={
                <SlimTissueCell terms={slimList} style={{ marginTop: '4px' }} />
              }
            />
          </Table>
        </Col>
        <Col md={9}>
          <Table
            rowHeight={40}
            rowsCount={filteredTerms.length}
            width={(genes.length + 1) * 40 + 150 + 200}
            height={40 * filteredTerms.length + 80}
            headerHeight={75}
            rowClassNameGetter={() => s.row}
          >
            <Column
              width={120}
              header={<Cell style={{ textAlign: 'center' }}>TISSUE</Cell>}
              cell={<TissueCell slim={slimTerms} terms={filteredTerms} />}
            />
            {genes.map(cols)}
            {genes.length > 1 ? (
              <Column
                header={
                  <Cell
                    className={s.geneHeader}
                    onClick={() => handleGeneSelect(null)}
                  >
                    <Tooltip text="Average tissue/cell-type expression confidence">
                      AVERAGE
                    </Tooltip>
                  </Cell>
                }
                width={40}
                cell={
                  <ProbCell
                    colIndex={genes.length}
                    onClick={() => handleGeneSelect(null)}
                    selected={!selectedGene}
                    border={genes.length === 1 ? null : '3px solid #555'}
                    terms={filteredTerms}
                    data={avgs}
                  />
                }
              />
            ) : null}
            <Column
              width={200}
              header={
                <Cell onClick={handleSort} className={s.header}>
                  {selectedGene
                    ? `${selectedGene.standard_name} CONFIDENCE`
                    : genes.length > 1
                    ? 'AVERAGE CONFIDENCE'
                    : null}
                  <Tooltip text="Sort tissues by confidence">
                    <i
                      style={{ marginLeft: '10px' }}
                      className="fa fa-sort-amount-desc"
                    />
                  </Tooltip>
                </Cell>
              }
              cell={
                <BarProbCell terms={filteredTerms} data={barScores || avgs} />
              }
            />
          </Table>
        </Col>
      </Row>
    </div>
  );
};

export default withStyles(s)(TissueExpressionHeatmap);
